import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LaunchIcon from '@mui/icons-material/Launch';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ListIcon from '@mui/icons-material/List';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import useForm from '../../../../components/_hook/useForm';
import useLanguage from '../../../../components/_hook/useLanguage';
import { useUpdateProductVariationsMutation } from '../../../../../graphql/mutation/product/updateProductVariationsMutation';
import useNotification from '../../../../components/_hook/useNotification';
import useRouter from '../../../../components/_hook/useRouter';
import { useProductQuery } from '../../../../../graphql/query/product/useProductQuery';
import NotFound from '../../../../components/_error/NotFound';
import Context from '../../../../context';
import useRoutes from '../../../../components/_hook/useRoutes';
import ProductLayout from '../../../../layout/Product';
import StickyFormFooter from '../../../../components/_form/StickyFormFooter';
import useKeepitalRoutes from '../../../../components/_hook/useKeepitalRoutes';
import { IProductVariationsSubTypeInput } from '../../../../../graphql/type/_inputTypes/productVariations';
import Helmet from '../../../../components/Helmet';
import { IProductCustomFieldsSubTypeInput } from '../../../../../graphql/type/_inputTypes/productCustomFields';
import { useCompanyQuery } from '../../../../../graphql/query/company/useCompanyQuery';
import Select, { IValue } from '../../../../components/_select/_module/Select';
import DeleteIconButton from '../../../../components/_iconButton/DeleteIconButton';
import random from '../../../../components/util/random';
import ProductVariationRamificationTableItem from './_module/ProductVariationRamificationTableItem';
import FormCreateUpdateProductRamification from './_module/FormCreateUpdateProductRamification';
import { useSyncCompanyProductCustomFieldMutation } from '../../../../../graphql/mutation/company/syncCompanyProductCustomFieldMutation';
import ProductVariationInventoryTableItem from './_module/ProductVariationInventoryTableItem';
import ProductDetailDropshippedProductInfo from '../_module/ProductDetailDropshippedProductInfo';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
    width: '100%',
  },
  contentCard: {
    width: '100%',
    backgroundColor: theme.palette.mode === 'light' ? '#f3f3f3' : 'inherit',
  },
  tab: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#000',
  },
}));

function ProductDetailVariation() {
  interface IFormValue {
    variationCustomFields: IProductCustomFieldsSubTypeInput[];
    variations: IProductVariationsSubTypeInput[];
  }

  const { t } = useTranslation();
  const classes = useStyles();
  const { notification } = useNotification();
  const { params, history } = useRouter();
  const { path } = useRoutes();
  const { company: contextCompany } = useContext(Context);
  const { docID: productId } = params;
  const { getText } = useLanguage();
  const { path: keepitalPath } = useKeepitalRoutes();
  const [tab, setTab] = useState(1);
  const [
    isOpenCreateProductRamificationDialog,
    setIsOpenCreateProductRamificationDialog,
  ] = useState(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const productFragment = `
            _id
            slug
            company
            name {
              la
              text
            }
            custom_fields {
              id
              status
            }
            isDropshippedProduct
            variations { 
              id
              attributes
              inventory {
                low_stock
                no_stock
                in_stock
                reserved
                lead_time
                ability_number
                ability_time
                availability
                date_from
                date_to
              }
              dropshipping {
                prices {
                  currency
                  final
                }
              }
            }
            prices {
              currency
            }
  `;
  const companyFragment = `
            _id
            origin
			slug
            custom_fields {
              id
              locked
              title {
                la
                text
              }
              description {
                la
                text
              }
              field_type
              data_type
              attributes_input
              attributes
              currency
            }
  `;
  const { data, loading } = useProductQuery(productFragment, {
    variables: {
      filter: {
        companyId: contextCompany._id,
        _id: productId as string,
      },
    },
  });

  const { data: companyData, loading: companyLoading } = useCompanyQuery(
    companyFragment,
    {
      variables: { filter: { _id: contextCompany._id as string } },
    }
  );

  function onCompleted() {
    notification.success(t('done'));
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    updateProductVariationsMutation,
    { loading: isUpdatingProduct },
  ] = useUpdateProductVariationsMutation(
    `updatedProduct {
				${productFragment}
			}`,
    {
      onCompleted: () => {
        onCompleted();
        setRefreshKey(new Date().getTime());
      },
      onError,
    }
  );

  const [
    syncCompanyProductCustomFieldMutation,
  ] = useSyncCompanyProductCustomFieldMutation(
    `updatedCompany {
				${companyFragment}
			}`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(tempValues: IFormValue) {
    if (data) {
      await updateProductVariationsMutation({
        variables: {
          _id: data.products.items[0]._id,
          variationCustomFields: tempValues.variationCustomFields,
          variations: tempValues.variations,
        },
      });
    }
  }

  const {
    values,
    setValue,
    handleSubmit,
    errors,
    setValues,
  } = useForm<IFormValue>({
    defaultValues: {
      variationCustomFields: [],
      variations: [],
    },
    onSubmit: onSubmitForm,
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (
        tempValues.variations.find((variation) => !variation.attributes.sku)
      ) {
        newErrors.variations = t('some_product_no_sku');
      }

      Object.keys(newErrors).forEach((newError) =>
        notification.error(newErrors[newError])
      );

      return newErrors;
    },
  });

  useEffect(() => {
    if (!loading && data && data.products.items.length) {
      const product = data.products.items[0];

      setValues({
        variationCustomFields:
          product.custom_fields.map((custom_field) => ({
            id: custom_field.id,
            status: custom_field.status,
          })) || [],
        variations: product.variations
          ? product.variations.map((variation) => {
              const inventory = variation.inventory || {};
              const dropshipping = variation.dropshipping || {};
              return {
                id: variation.id,
                attributes: variation.attributes,
                inventoryLowStock: inventory.low_stock,
                inventoryNoStock: inventory.no_stock,
                inventoryInStock: inventory.in_stock,
                inventoryReserved: inventory.reserved,
                inventoryLeadTime: inventory.lead_time,
                inventoryAbilityNumber: inventory.ability_number,
                inventoryAbilityTime: inventory.ability_time,
                inventoryAvailability: inventory.availability,
                inventoryDateFrom: inventory.date_from,
                inventoryDateTo: inventory.date_to,
                dropshippingPriceCurrency: dropshipping.prices
                  ? dropshipping.prices.currency
                  : undefined,
                dropshippingPriceFinal: dropshipping.prices
                  ? dropshipping.prices.final
                  : undefined,
              };
            })
          : [],
      });
    }
  }, [loading]);

  useEffect(() => {
    if (!companyLoading && companyData && companyData.companies.items.length) {
      const company = companyData.companies.items[0];

      if (!company.custom_fields || !company.custom_fields.length) {
        syncCompanyProductCustomFieldMutation({
          variables: {
            companyId: company._id,
          },
        });
      }
    }
  }, [companyLoading]);

  if (loading || companyLoading) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  const product = productId && data ? data.products.items[0] : null;
  const company = companyData ? companyData.companies.items[0] : null;

  if (!product || !company) {
    return <NotFound />;
  }

  if (product && product.isDropshippedProduct) {
    return <ProductDetailDropshippedProductInfo />;
  }

  const companyCustomFields = company.custom_fields || [];
  const variationCustomFields = values.variationCustomFields.filter(
    (variationCustomField) =>
      variationCustomField.status || variationCustomField.id === 'sku'
  );

  return (
    <Paper square className={classes.paper}>
      <Helmet title={`${t('variation', { count: 2 })} | ${t('product')}`} />
      <Dialog
        open={isOpenCreateProductRamificationDialog}
        onClose={() => setIsOpenCreateProductRamificationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <FormCreateUpdateProductRamification
          onCancel={() => {
            setIsOpenCreateProductRamificationDialog(false);
          }}
          onSubmit={() => {
            setIsOpenCreateProductRamificationDialog(false);
          }}
        />
      </Dialog>
      <Grid container spacing={2}>
        <Grid container justifyContent="space-between" spacing={1}>
          <Grid item>
            <Typography variant="h6">
              {t('update')} {getText(product.name)} {t('product')}{' '}
              {t('variation')}
            </Typography>
          </Grid>
          {product && (
            <Grid item>
              <Button
                size="small"
                color="primary"
                variant="contained"
                startIcon={<LaunchIcon />}
                component="a"
                href={keepitalPath('company.showcase.product.index', {
                  origin: company.origin ? company.origin.toLowerCase() : '',
                  companySlug: company.slug ? company.slug : '',
                  productSlug: product.slug,
                })}
                target="_blank"
              >
                {t('view')}
              </Button>
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Card className={classes.contentCard}>
            <CardContent>
              <Grid container item xs={12} spacing={1}>
                <Grid item xs={12}>
                  <Tabs
                    className={classes.tab}
                    value={tab}
                    onChange={(e, newValue) => setTab(newValue)}
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    <Tab label={t('list')} value={1} icon={<ListIcon />} />
                    <Tab
                      label={t('ramification')}
                      value={2}
                      icon={<ListAltIcon />}
                    />
                    <Tab
                      label={t('inventory')}
                      value={3}
                      icon={<LocalShippingIcon />}
                    />
                  </Tabs>
                </Grid>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Grid item xs={12}>
                        {tab === 1 && (
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  {!variationCustomFields.length && (
                                    <TableCell align="center" colSpan={10}>
                                      {t(
                                        'you_dont_have_custom_field_activated'
                                      )}
                                    </TableCell>
                                  )}
                                  {variationCustomFields.map(
                                    (variationCustomField) => {
                                      const companyCustomField = companyCustomFields.find(
                                        (customField) =>
                                          customField.id ===
                                          variationCustomField.id
                                      );

                                      if (!companyCustomField) return null;
                                      return (
                                        <TableCell key={companyCustomField.id}>
                                          <Tooltip
                                            title={
                                              companyCustomField.description
                                                ? getText(
                                                    companyCustomField.description
                                                  )
                                                : ''
                                            }
                                          >
                                            <span>
                                              {getText(
                                                companyCustomField.title
                                              )}
                                            </span>
                                          </Tooltip>
                                        </TableCell>
                                      );
                                    }
                                  )}
                                  {variationCustomFields.length > 0 && (
                                    <TableCell>{t('action')}</TableCell>
                                  )}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {values.variations.map((variation) => {
                                  const attributes = variation.attributes
                                    ? variation.attributes
                                    : {};
                                  return (
                                    <TableRow key={variation.id}>
                                      {variationCustomFields.map(
                                        (variationCustomField) => {
                                          const value =
                                            attributes[variationCustomField.id];
                                          const companyCustomField = companyCustomFields.find(
                                            (customField) =>
                                              customField.id ===
                                              variationCustomField.id
                                          );

                                          if (!companyCustomField) return null;

                                          const companyCustomFieldAttributes =
                                            companyCustomField.attributes || [];

                                          const onChange = (
                                            newValue: string
                                          ) => {
                                            const tempVariations = values.variations.map(
                                              (tempVariation) => {
                                                if (
                                                  tempVariation.id ===
                                                  variation.id
                                                ) {
                                                  tempVariation.attributes = tempVariation.attributes
                                                    ? {
                                                        ...tempVariation.attributes,
                                                      }
                                                    : {};
                                                  tempVariation.attributes[
                                                    variationCustomField.id
                                                  ] = newValue;
                                                }
                                                return tempVariation;
                                              }
                                            );

                                            setValue(
                                              'variations',
                                              tempVariations
                                            );
                                          };

                                          return (
                                            <TableCell
                                              key={variationCustomField.id}
                                            >
                                              {companyCustomField.field_type ===
                                                'input' && (
                                                <TextField
                                                  fullWidth
                                                  type={
                                                    companyCustomField.data_type ===
                                                    'string'
                                                      ? 'text'
                                                      : companyCustomField.data_type
                                                  }
                                                  value={value}
                                                  placeholder={
                                                    companyCustomField.description
                                                      ? getText(
                                                          companyCustomField.description
                                                        )
                                                      : ''
                                                  }
                                                  onChange={(e) => {
                                                    onChange(e.target.value);
                                                  }}
                                                  InputProps={
                                                    variationCustomField.id ===
                                                    'prices'
                                                      ? {
                                                          startAdornment: (
                                                            <InputAdornment position="start">
                                                              {product.prices &&
                                                              product.prices
                                                                .currency
                                                                ? product.prices
                                                                    .currency
                                                                : ''}
                                                            </InputAdornment>
                                                          ),
                                                        }
                                                      : undefined
                                                  }
                                                  error={
                                                    variationCustomField.id ===
                                                      'prices' &&
                                                    (!product.prices ||
                                                      !product.prices.currency)
                                                  }
                                                  helperText={
                                                    variationCustomField.id ===
                                                      'prices' &&
                                                    (!product.prices ||
                                                      !product.prices.currency)
                                                      ? t(
                                                          '__type__is_required',
                                                          {
                                                            type: `${t(
                                                              'product'
                                                            )} ${t(
                                                              'currency'
                                                            )}`,
                                                          }
                                                        )
                                                      : undefined
                                                  }
                                                />
                                              )}
                                              {companyCustomField.field_type ===
                                                'dropdown' && (
                                                <Select<IValue>
                                                  isClearable
                                                  options={companyCustomFieldAttributes.map(
                                                    (
                                                      companyCustomFieldAttribute
                                                    ) => ({
                                                      label: companyCustomFieldAttribute,
                                                      value: companyCustomFieldAttribute,
                                                    })
                                                  )}
                                                  fullWidth
                                                  menuPosition="fixed"
                                                  value={value}
                                                  onChange={(option) => {
                                                    onChange(
                                                      option ? option.value : ''
                                                    );
                                                  }}
                                                />
                                              )}
                                            </TableCell>
                                          );
                                        }
                                      )}
                                      <TableCell>
                                        <DeleteIconButton
                                          tooltip={t('delete')}
                                          onClick={() =>
                                            setValue(
                                              'variations',
                                              values.variations.filter(
                                                (tempVariation) =>
                                                  tempVariation.id !==
                                                  variation.id
                                              )
                                            )
                                          }
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                                {variationCustomFields.length > 0 && (
                                  <TableRow>
                                    <TableCell align="center" colSpan={10}>
                                      <Button
                                        fullWidth
                                        color="primary"
                                        variant="contained"
                                        onClick={() => {
                                          setValue('variations', [
                                            ...values.variations,
                                            {
                                              id: random.id(8),
                                              attributes: {},
                                            },
                                          ]);
                                        }}
                                        startIcon={<AddIcon />}
                                      >
                                        {t('add')}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                        {tab === 2 && (
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>{t('Title')}</TableCell>
                                  <TableCell>{t('Description')}</TableCell>
                                  <TableCell>{t('Field Type')}</TableCell>
                                  <TableCell>{t('Data Type')}</TableCell>
                                  <TableCell>{t('action')}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {companyCustomFields.map(
                                  (companyCustomField) => (
                                    <TableRow key={companyCustomField.id}>
                                      <ProductVariationRamificationTableItem
                                        productVariationCustomFields={
                                          variationCustomFields
                                        }
                                        companyId={company._id}
                                        item={companyCustomField}
                                        onPublish={(
                                          tempVariationCustomField
                                        ) => {
                                          let isExists = false;
                                          const tempVariationCustomFields = values.variationCustomFields.map(
                                            (variationCustomField) => {
                                              if (
                                                variationCustomField.id ===
                                                tempVariationCustomField.id
                                              ) {
                                                isExists = true;
                                                variationCustomField.status = true;
                                              }

                                              return variationCustomField;
                                            }
                                          );

                                          if (!isExists) {
                                            const newVariationCustomField = {
                                              id: tempVariationCustomField.id,
                                              status: true,
                                            };
                                            if (
                                              tempVariationCustomField.id ===
                                              'sku'
                                            ) {
                                              tempVariationCustomFields.unshift(
                                                newVariationCustomField
                                              );
                                            } else if (
                                              tempVariationCustomField.id ===
                                              'prices'
                                            ) {
                                              tempVariationCustomFields.push(
                                                newVariationCustomField
                                              );
                                            } else if (
                                              tempVariationCustomFields.length >
                                                0 &&
                                              tempVariationCustomFields[
                                                tempVariationCustomFields.length -
                                                  1
                                              ].id !== 'prices'
                                            ) {
                                              tempVariationCustomFields.push(
                                                newVariationCustomField
                                              );
                                            } else {
                                              tempVariationCustomFields.splice(
                                                tempVariationCustomFields.length -
                                                  1,
                                                0,
                                                newVariationCustomField
                                              );
                                            }
                                          }

                                          setValue(
                                            'variationCustomFields',
                                            tempVariationCustomFields
                                          );
                                        }}
                                        onUnpublish={(
                                          tempVariationCustomField
                                        ) => {
                                          setValue(
                                            'variationCustomFields',
                                            values.variationCustomFields.map(
                                              (variationCustomField) => {
                                                const cloneVariationCustomField = {
                                                  ...variationCustomField,
                                                };
                                                if (
                                                  variationCustomField.id ===
                                                  tempVariationCustomField.id
                                                ) {
                                                  cloneVariationCustomField.status = false;
                                                }

                                                return cloneVariationCustomField;
                                              }
                                            )
                                          );
                                        }}
                                      />
                                    </TableRow>
                                  )
                                )}
                                <TableRow>
                                  <TableCell align="center" colSpan={10}>
                                    <Button
                                      fullWidth
                                      color="primary"
                                      variant="contained"
                                      onClick={() => {
                                        setIsOpenCreateProductRamificationDialog(
                                          true
                                        );
                                      }}
                                      startIcon={<AddIcon />}
                                    >
                                      {t('add')}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                        {tab === 3 && (
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>SKU</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {values.variations.map((variation) => (
                                  <ProductVariationInventoryTableItem
                                    key={variation.id}
                                    refreshKey={refreshKey}
                                    item={variation}
                                    onChange={(tempVariation) => {
                                      const tempVariations = values.variations.map(
                                        (tempVariation1) => {
                                          if (
                                            tempVariation.id ===
                                            tempVariation1.id
                                          ) {
                                            return tempVariation;
                                          }
                                          return tempVariation1;
                                        }
                                      );

                                      setValue('variations', tempVariations);
                                    }}
                                  />
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <StickyFormFooter
          onCancel={() =>
            history.push(
              path('product.list', {
                companyID: company._id,
              })
            )
          }
          onSubmit={handleSubmit}
          isSubmitting={isUpdatingProduct}
        />
      </Grid>
    </Paper>
  );
}

export default () => <ProductLayout main={<ProductDetailVariation />} />;
